import { DateFormatter } from '@repo/toolkit/date'
import { NumberFormatter } from '@repo/toolkit/number'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import { MuseumIcon, PlusIcon } from '@repo/ui/icons'
import Modal, { MODAL_CONTENT_CLASSNAME } from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'
import { useMemo, useRef } from 'react'

import { API } from '../../../../../api/apis.config'
import { createCulturalOuting } from '../../../../../api/cultural-outings/create-cultural-outing'
import { updateCulturalOuting } from '../../../../../api/cultural-outings/update-cultural-outing'
import useAuth from '../../../../../lib/hooks/use-auth'
import { useModalManager } from '../../../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../../../lib/hooks/use-translate'
import { outingDurationEnumMapping } from '../../../../../lib/types/enums/cultural-outing/outing-duration.enum'
import {
  CulturalOuting,
  CulturalOutingFilters
} from '../../../../../lib/types/models/cultural-outing/cultual-outing'
import { modalButtonConfig } from '../../../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../../../lib/utils/functions/misc.functions'
import { DynamicTableRef } from '../../../../elements/dynamic-table'
import NestedDataTable from '../../../../elements/nested-table'
import CulturalOutingForm from './cultural-outing-form'

type Props = {
  olId: number
  coachCompanyId: number
}

const CulturalOutingTable = ({ olId, coachCompanyId }: Props) => {
  const tableRef = useRef<DynamicTableRef<CulturalOuting>>(null)
  const { t } = useTranslate()
  const { user } = useAuth()
  const currency = user?.ol?.currency

  const columns: Column<CulturalOuting>[] = [
    {
      accessorKey: 'activity.name',
      header: 'Trip cultural outings/activities',
      enableSorting: true,
      cell: ({ row }) => row.original.activity.name
    },
    {
      accessorKey: 'numberOfSeats',
      header: 'Seats',
      enableSorting: true
    },
    {
      accessorKey: 'duration',
      header: 'Duration',
      enableSorting: true,
      cell: ({ row }) => {
        const duration = row.original.duration
        return <Badge text={t(outingDurationEnumMapping[duration])} />
      }
    },
    {
      accessorKey: 'comment',
      header: 'Comments',
      enableSorting: true,
      cell: ({ row }) => row.original.comment || '-'
    },
    {
      accessorKey: 'cost',
      header: 'Cost',
      enableSorting: true,
      cell: ({ row }) => {
        const cost = NumberFormatter.formatCurrency(row.original.cost, currency)
        return <Badge text={cost} variant="primary" />
      }
    },
    {
      accessorKey: 'validUntil',
      header: 'Valid until',
      enableSorting: true,
      cell: ({ row }) => {
        const validUntil = row.original.validUntil
          ? DateFormatter.DDMMYYYY(row.original.validUntil)
          : '-'
        return <Badge text={validUntil} />
      }
    }
  ]

  const { selectedData, modalState, toggleModal, closeModel } = useModalManager<
    'create' | 'edit',
    CulturalOuting
  >(['create', 'edit'])

  const { mutateAsync: createCulturalOutingMutation, isPending: isCreateCulturalOutingPending } =
    useMutation({
      mutationFn: createCulturalOuting,
      onError: showAxiosError,
      onSuccess: () => {
        tableRef.current?.refetch()
        closeModel('create')
      }
    })

  const { mutateAsync: updateCulturalOutingMutation, isPending: isUpdateCulturalOutingPending } =
    useMutation({
      mutationFn: updateCulturalOuting,
      onError: showAxiosError,
      onSuccess: () => {
        tableRef.current?.refetch()
        closeModel('edit')
      }
    })

  return (
    <>
      <NestedDataTable<CulturalOuting, CulturalOutingFilters>
        ref={tableRef}
        overflow
        variant="action"
        deleteMessage={'Are you sure you want to delete this cultural outing?'}
        title={'Trip cultural outings/activities'}
        actions={[
          {
            icon: PlusIcon,
            variant: 'secondary',
            label: 'Add a cultural outings / activities',
            fn: () => toggleModal('create')
          }
        ]}
        columns={columns}
        onEditAction={(payment) => {
          toggleModal('edit', payment)
        }}
        endpoint={API.culturalOutings.getAll.url}
        filters={useMemo(() => ({ coachCompanyId }), [coachCompanyId])}
      />

      <Modal
        isOpen={modalState.create}
        title={'Add a trip for a cultural outing / activity'}
        icon={MuseumIcon}
        hideActions
        size="medium"
        onCancel={() => closeModel('create')}
      >
        <CulturalOutingForm
          olId={olId}
          className={MODAL_CONTENT_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            onConfirm: async (values) => {
              await createCulturalOutingMutation({ coachCompanyId, ...values })
            },
            onCancel: () => closeModel('create'),
            isLoading: isCreateCulturalOutingPending,
            isEnglish: true
          })}
        />
      </Modal>

      {selectedData && (
        <Modal
          isOpen={modalState.edit}
          title={'Modify a trip for a cultural outing / activity'}
          icon={MuseumIcon}
          hideActions
          size="medium"
          onCancel={() => closeModel('edit')}
        >
          <CulturalOutingForm
            olId={olId}
            className={MODAL_CONTENT_CLASSNAME}
            defaultValues={selectedData}
            buttonsConfig={modalButtonConfig({
              onConfirm: async (values) => {
                await updateCulturalOutingMutation({
                  id: selectedData.id,
                  coachCompanyId,
                  ...values
                })
              },
              onCancel: () => closeModel('edit'),
              isLoading: isUpdateCulturalOutingPending,
              isEnglish: true
            })}
          />
        </Modal>
      )}
    </>
  )
}

export default CulturalOutingTable
